import { Link, useLocation, useRouter } from "@tanstack/react-router";
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  Clock,
  Cpu,
  FolderKanban,
  LayoutDashboard,
  LogOut,
  PanelLeft,
  PanelLeftClose,
  Settings,
  UserRound,
  Users,
} from "lucide-react";
import { useState } from "react";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Button } from "@/components/ui/button";
import { useMyAccess } from "@/hooks/use-my-access";
import { signOut, useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { useSidebar } from "./SidebarContext";

interface NavItem {
  label: string;
  icon: typeof LayoutDashboard;
  href: string;
  disabled?: boolean;
  /** Shown to Aggregate users (Client Partners, CSMs): their own book only. */
  aggregate?: boolean;
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { data: session } = useSession();
  const router = useRouter();
  const location = useLocation();
  const { isMobile, mobileOpen, setMobileOpen } = useSidebar();

  const navItems: NavItem[] = [
    { label: "My Dashboard", icon: LayoutDashboard, href: "/dashboard" },
    { label: "Projects", icon: FolderKanban, href: "/dashboard/projects", aggregate: true },
    { label: "Accounts", icon: Building2, href: "/dashboard/accounts", aggregate: true },
    { label: "Client Partners", icon: UserRound, href: "/dashboard/client-partners" },
    // Risk sits directly under the delivery screens rather than at the bottom:
    // it is the only forward-looking board in the product, and everything
    // above it reports what already happened.
    { label: "At Risk", icon: AlertTriangle, href: "/dashboard/risk", aggregate: true },
    // Beside At Risk: the other forward-looking board, and the only live one.
    // Aggregate users get it too -- the engine sends them their book, margin
    // percent only.
    {
      label: "This month",
      icon: CalendarClock,
      href: "/dashboard/this-month",
      aggregate: true,
    },
    { label: "Technology", icon: Cpu, href: "/dashboard/technology" },
    { label: "Bench", icon: Users, href: "/dashboard/bench" },
    // Beside Bench, and after it: both boards are about capacity that earns
    // nothing, and this is the same question asked per person rather than per
    // offering. It goes last because it is the only board that is hours all the
    // way down -- no money on it anywhere.
    { label: "Internal Time", icon: Clock, href: "/dashboard/internal-time" },
  ];
  const isAdmin = session?.user?.role === "admin";
  // Aggregate users get their book's screens only, Accounts first since it is
  // their landing page. Until the level is known the narrower list shows, so a
  // Client Partner never sees a link the engine would refuse.
  const { data: access } = useMyAccess();
  const isAggregate = access?.isAggregate ?? true;
  const visibleNavItems = isAggregate
    ? (
        [
          "/dashboard/accounts",
          "/dashboard/projects",
          "/dashboard/risk",
          "/dashboard/this-month",
        ] as const
      ).flatMap((href) => navItems.filter((item) => item.href === href && item.aggregate))
    : navItems;

  async function handleSignOut() {
    await signOut();
    router.navigate({ to: "/login" });
  }

  function handleNavClick() {
    if (isMobile) {
      setMobileOpen(false);
    }
  }

  // On mobile, don't show collapsed state — always show full sidebar
  const showLabels = isMobile ? true : !collapsed;

  const sidebarContent = (
    <aside
      className={cn(
        "flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-200",
        isMobile ? "w-64 h-full" : collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Brand. The white lockup when the rail is open, the white fox glyph when
          collapsed -- both are the on-navy variants from the Nymbl bundle. */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        {showLabels ? (
          <img
            src="/brand/Nymbl_White_Main_Logo.png"
            alt="Nymbl"
            className="h-7 w-auto object-contain"
          />
        ) : (
          <img
            src="/brand/Nymbl_white_fox_button.png"
            alt="Nymbl"
            className="h-7 w-7 object-contain"
          />
        )}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-sidebar-foreground shrink-0"
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-2">
        {visibleNavItems.map((item) => {
          const isActive =
            item.href === "/dashboard"
              ? location.pathname === "/dashboard"
              : location.pathname.startsWith(item.href);

          return (
            <Link
              key={item.label}
              to={item.href}
              onClick={handleNavClick}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive ? "bg-sidebar-accent text-sidebar-primary" : "text-sidebar-foreground/60",
                item.disabled && "pointer-events-none opacity-40",
                !item.disabled &&
                  !isActive &&
                  "hover:bg-sidebar-accent hover:text-sidebar-foreground",
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {showLabels && <span>{item.label}</span>}
              {showLabels && item.disabled && (
                <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">
                  Soon
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User info + sign out */}
      <div className="border-t border-sidebar-border p-2">
        {session?.user && (
          <Link
            to="/dashboard/profile"
            onClick={handleNavClick}
            className={cn(
              "mb-2 flex items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-sidebar-accent",
              !showLabels && "justify-center",
            )}
          >
            <UserAvatar user={session.user} className="h-8 w-8" />
            {showLabels && (
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-sidebar-foreground">
                  {session.user.name}
                </p>
                <p className="truncate text-xs text-muted-foreground">{session.user.email}</p>
              </div>
            )}
          </Link>
        )}
        <div className={cn("flex gap-2", !showLabels && "flex-col")}>
          {/* Admins only. Not shown greyed out to everyone else: a control a
              person can never use is noise, and for a Client Partner it
              advertises a part of the product they have no access to. */}
          {isAdmin ? (
            <Link
              to="/dashboard/settings"
              onClick={handleNavClick}
              className={cn(
                "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                location.pathname.startsWith("/dashboard/settings")
                  ? "bg-sidebar-accent text-sidebar-primary"
                  : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground",
              )}
            >
              <Settings className="h-4 w-4 shrink-0" />
              {showLabels && <span>Settings</span>}
            </Link>
          ) : null}
          <button
            type="button"
            onClick={handleSignOut}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors",
            )}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {showLabels && <span>Sign Out</span>}
          </button>
        </div>
      </div>
    </aside>
  );

  // Mobile: overlay sidebar
  if (isMobile) {
    if (!mobileOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
        {/* Slide-out sidebar */}
        <div className="fixed left-0 top-0 z-50 h-full">{sidebarContent}</div>
      </>
    );
  }

  // Desktop: normal sidebar
  return sidebarContent;
}
